import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { DatePicker } from '@mui/x-date-pickers-pro';
import dayjs from 'dayjs';

import SftGlobalData from '../../library/SftGlobalData';

// ─────────────────────────────────────────────────────────────────────────────
// Leaflet (loaded once from CDN — no build/npm dependency, mirrors the script)
// ─────────────────────────────────────────────────────────────────────────────
let leafletPromise = null;
const loadLeaflet = () => {
    if (typeof window !== 'undefined' && window.L) return Promise.resolve(window.L);
    if (leafletPromise) return leafletPromise;
    leafletPromise = new Promise((resolve, reject) => {
        const css = document.createElement('link');
        css.rel = 'stylesheet';
        css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(css);
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.async = true;
        script.onload = () => resolve(window.L);
        script.onerror = () => { leafletPromise = null; reject(new Error('Leaflet load failed')); };
        document.head.appendChild(script);
    });
    return leafletPromise;
};

// GeoJSON (~25 MB) + postcode→city names — fetched once, cached at module level.
let geoCache = null;
let namesCache = null;
const loadGeo = () => {
    const base = process.env.PUBLIC_URL || '';
    const geo = geoCache
        ? Promise.resolve(geoCache)
        : fetch(base + '/geo/postal_codes_be.geojson').then(r => { if (!r.ok) throw new Error('GeoJSON HTTP ' + r.status); return r.json(); }).then(j => { geoCache = j; return j; });
    const names = namesCache
        ? Promise.resolve(namesCache)
        : fetch(base + '/geo/postcode_namen.json').then(r => r.ok ? r.json() : {}).then(j => { namesCache = j || {}; return namesCache; }).catch(() => { namesCache = {}; return {}; });
    return Promise.all([geo, names]);
};

// Turf (loaded once from CDN) — used to dissolve postcode polygons into one shape per city.
let turfPromise = null;
const loadTurf = () => {
    if (typeof window !== 'undefined' && window.turf) return Promise.resolve(window.turf);
    if (turfPromise) return turfPromise;
    turfPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/@turf/turf@6.5.0/turf.min.js';
        script.async = true;
        script.onload = () => resolve(window.turf);
        script.onerror = () => { turfPromise = null; reject(new Error('Turf load failed')); };
        document.head.appendChild(script);
    });
    return turfPromise;
};

// Dissolve the postcode polygons into one (Multi)Polygon per city by unioning every
// feature that shares a city name. Heavy, but done once and cached at module level.
let mergedCache = null;
const buildMergedCollection = (turf, geojson, pcCity) => {
    if (mergedCache) return mergedCache;
    const groups = {};
    for (const f of geojson.features) {
        const city = pcCity[f.properties.postcode] || f.properties.postcode;
        (groups[city] = groups[city] || []).push(f);
    }
    const mk = (geometry, city, postcodes) => ({ type: 'Feature', geometry, properties: { city, postcodes } });
    const features = [];
    for (const city in groups) {
        const fs = groups[city];
        const postcodes = [...new Set(fs.map(f => f.properties.postcode))].sort();
        let acc = null;
        for (const f of fs) {
            if (!acc) { acc = f; continue; }
            try {
                const u = turf.union(acc, f);
                if (u) { acc = u; } else { features.push(mk(f.geometry, city, postcodes)); }
            } catch (e) {
                features.push(mk(f.geometry, city, postcodes)); // keep geometry even if union fails
            }
        }
        if (acc) features.push(mk(acc.geometry, city, postcodes));
    }
    mergedCache = { type: 'FeatureCollection', features };
    return mergedCache;
};

// ─────────────────────────────────────────────────────────────────────────────
// Choropleth helpers (ported verbatim from reporting/kaart/kaart.php)
// ─────────────────────────────────────────────────────────────────────────────
const KLEUREN = ['#eff3ff', '#bdd7e7', '#6baed6', '#2171b5', '#084594'];

const interpoleerKleur = (hex1, hex2, t) => {
    const r1 = parseInt(hex1.slice(1, 3), 16), g1 = parseInt(hex1.slice(3, 5), 16), b1 = parseInt(hex1.slice(5, 7), 16);
    const r2 = parseInt(hex2.slice(1, 3), 16), g2 = parseInt(hex2.slice(3, 5), 16), b2 = parseInt(hex2.slice(5, 7), 16);
    const r = Math.round(r1 + (r2 - r1) * t).toString(16).padStart(2, '0');
    const g = Math.round(g1 + (g2 - g1) * t).toString(16).padStart(2, '0');
    const b = Math.round(b1 + (b2 - b1) * t).toString(16).padStart(2, '0');
    return `#${r}${g}${b}`;
};

const omzetNaarKleur = (omzet, min, max) => {
    if (!omzet || omzet <= 0) return '#e8e8e8';
    const t = Math.min(1, Math.max(0, Math.sqrt((omzet - min) / (max - min || 1))));
    const n = KLEUREN.length - 1;
    const i = Math.min(Math.floor(t * n), n - 1);
    const f = t * n - i;
    return interpoleerKleur(KLEUREN[i], KLEUREN[Math.min(i + 1, n)], f);
};

const formateerOmzet = (bedrag) => '€ ' + Math.round(bedrag).toLocaleString('nl-BE');

const icon = { checkedIcon: <CheckBoxIcon fontSize="small" />, icon: <CheckBoxOutlineBlankIcon fontSize="small" /> };

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
const RevenueMapModal = (props) => {
    const { open, onClose } = props;

    const tx = (k, fallback) => {
        const v = props.t ? props.t(k) : k;
        return (v === k || v === undefined) ? (fallback ?? k) : v;
    };

    const [loadingFilters, setLoadingFilters] = useState(false);
    const [stores, setStores] = useState([]);       // [{code, oms}]
    const [registers, setRegisters] = useState([]); // [{code, mag, oms}]
    const [selectedStores, setSelectedStores] = useState([]);       // [code]
    const [selectedRegisters, setSelectedRegisters] = useState([]); // [code]

    const [van, setVan] = useState(dayjs().startOf('year'));
    const [tot, setTot] = useState(dayjs());

    const [loadingMap, setLoadingMap] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [mapReady, setMapReady] = useState(false);

    // Leaflet handles + live data live in refs so the (stable) style/hover closures read fresh values.
    const containerRef = useRef(null);
    const tooltipRef = useRef(null);
    const mapRef = useRef(null);
    const layerRef = useRef(null);
    const legendRef = useRef(null);
    const namesRef = useRef({});
    const dataRef = useRef({});       // {postcode: omzet}
    const pcCityRef = useRef({});     // {postcode: cityName}  (cities span multiple postcodes)
    const cityTotalsRef = useRef({}); // {cityName: summed omzet}
    const minRef = useRef(0);
    const maxRef = useRef(1);
    const reqIdRef = useRef(0);

    // ── Registers available for the selected shops (synced like syncKassasMetWinkels) ─────
    const availableRegisters = useMemo(
        () => registers.filter(r => selectedStores.includes(r.mag)),
        [registers, selectedStores]
    );

    // When the shop selection changes, (re)check every visible register — matches the script.
    useEffect(() => {
        setSelectedRegisters(availableRegisters.map(r => r.code));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedStores, registers]);

    // ── Load filter options when the popup opens ──────────────────────────────
    useEffect(() => {
        if (!open) return;
        setErrorMessage(null);
        setLoadingFilters(true);
        SftGlobalData.post('webreports&action=mapFilters', { key: props.api }).then(response => {
            if (typeof response.error === 'undefined' && response?.data) {
                const st = Array.isArray(response.data.stores) ? response.data.stores : [];
                const rg = Array.isArray(response.data.registers) ? response.data.registers : [];
                setStores(st);
                setRegisters(rg);
                setSelectedStores(st.map(s => s.code)); // default: all shops (→ all registers via effect)
            } else {
                setErrorMessage(response?.message || tx('webreport_map_filtersError', 'Could not load map filters'));
            }
            setLoadingFilters(false);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, props.api]);

    // Aggregate per-postcode revenue into per-city totals and recompute the color scale
    // from those city totals (so the scale reflects grouped values, not single postcodes).
    const rebuildCityTotals = useCallback(() => {
        const totals = {};
        const pcCity = pcCityRef.current;
        for (const pc in dataRef.current) {
            const city = pcCity[pc] || pc;
            totals[city] = (totals[city] || 0) + (dataRef.current[pc] || 0);
        }
        cityTotalsRef.current = totals;
        const waarden = Object.values(totals).filter(v => v > 0);
        minRef.current = waarden.length ? Math.min(...waarden) : 0;
        maxRef.current = waarden.length ? Math.max(...waarden) : 1;
    }, []);

    // ── Build / tear down the Leaflet map ─────────────────────────────────────
    const buildMap = useCallback(async () => {
        if (mapRef.current || !containerRef.current) return;
        let L, geojsonRaw, names, turf = null;
        try {
            [L] = [await loadLeaflet()];
            [geojsonRaw, names] = await loadGeo();
        } catch (e) {
            setErrorMessage(tx('webreport_map_loadError', 'Could not load the map') + ': ' + e.message);
            return;
        }
        try { turf = await loadTurf(); } catch (e) { turf = null; } // dissolve is best-effort
        if (!containerRef.current || mapRef.current) return; // popup may have closed meanwhile
        namesRef.current = names || {};

        const MAX_BOUNDS = L.latLngBounds(L.latLng(49.04, 1.89), L.latLng(51.96, 7.06));
        const kaart = L.map(containerRef.current, {
            maxBounds: MAX_BOUNDS,
            maxBoundsViscosity: 1.0,
            minZoom: 7,
        }).setView([50.5, 4.5], 8);
        mapRef.current = kaart;

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
            maxZoom: 18,
        }).addTo(kaart);

        // Keep only Belgian postcodes — the dataset also carries some bordering French
        // communes (5-digit codes); Belgian postcodes are always 4 digits (1000–9999).
        const geojson = {
            ...geojsonRaw,
            features: (geojsonRaw.features || []).filter(f =>
                f.geometry && f.geometry.coordinates && /^[1-9][0-9]{3}$/.test(String(f.properties.postcode))
            ),
        };

        // Resolve every postcode → its city name once (cities cover several postcodes, e.g.
        // Antwerpen = 2000/2018/2060/…). Revenue is then aggregated per city below.
        const pcCity = {};
        for (const f of geojson.features) {
            const pc = f.properties.postcode;
            const geoNaam = f.properties.naam && f.properties.naam !== pc
                ? String(f.properties.naam).replace(/^\d{4}\s*/, '').trim()
                : null;
            pcCity[pc] = namesRef.current[pc] || geoNaam || pc;
        }
        pcCityRef.current = pcCity;
        rebuildCityTotals();

        // Dissolve postcode polygons into one shape per city (cached). If Turf failed to load,
        // fall back to the raw per-postcode features tagged with their city so coloring still works.
        let renderGeojson;
        if (turf) {
            renderGeojson = buildMergedCollection(turf, geojson, pcCity);
        } else {
            renderGeojson = {
                type: 'FeatureCollection',
                features: geojson.features.map(f => ({
                    type: 'Feature',
                    geometry: f.geometry,
                    properties: { city: pcCity[f.properties.postcode] || f.properties.postcode, postcodes: [f.properties.postcode] },
                })),
            };
        }

        const stijl = (feature) => {
            const omzet = cityTotalsRef.current[feature.properties.city];
            return {
                fillColor: omzetNaarKleur(omzet, minRef.current, maxRef.current),
                fillOpacity: omzet ? 0.80 : 0.25,
                color: '#fff',
                weight: 0.5,
                opacity: 0.7,
            };
        };

        // DOM-managed tooltip (mirrors kaart.php). Leaflet's sticky tooltip + bringToFront can
        // miss mouseout and get stuck, so we drive a single tip element ourselves and force-hide
        // it whenever the pointer leaves a feature or the map entirely.
        const hideTip = () => { if (tooltipRef.current) tooltipRef.current.style.display = 'none'; };
        const noDataLabel = tx('webreport_map_noData', 'No data');

        let actieveLaag = null;
        const resetActief = () => {
            if (actieveLaag) { laag.resetStyle(actieveLaag); actieveLaag = null; }
        };
        const laag = L.geoJSON(renderGeojson, {
            style: stijl,
            onEachFeature: (feature, layer) => {
                const city = feature.properties.city;
                const cityPostcodes = feature.properties.postcodes || [];
                const pcLabel = cityPostcodes.length > 1 ? cityPostcodes.join(', ') : (cityPostcodes[0] || city);
                layer.on('mouseover', function () {
                    if (actieveLaag && actieveLaag !== this) laag.resetStyle(actieveLaag);
                    actieveLaag = this;
                    const omzet = cityTotalsRef.current[city];
                    this.setStyle({ color: '#222', weight: 2, fillOpacity: omzet ? 0.95 : 0.4 });
                    this.bringToFront();
                    if (tooltipRef.current) {
                        tooltipRef.current.innerHTML =
                            `<div style="font-weight:700;font-size:14px;margin-bottom:2px;">${city}</div>`
                            + `<div style="font-size:11px;color:#aaa;margin-bottom:5px;">${pcLabel}</div>`
                            + `<div style="font-size:15px;font-weight:700;color:#7eb8f7;">${omzet ? formateerOmzet(omzet) : noDataLabel}</div>`;
                        tooltipRef.current.style.display = 'block';
                    }
                });
                layer.on('mouseout', function () {
                    laag.resetStyle(this);
                    if (actieveLaag === this) actieveLaag = null;
                    hideTip();
                });
            },
        }).addTo(kaart);
        layerRef.current = laag;

        // Position the tip next to the cursor; force-hide when the pointer leaves the map.
        kaart.on('mousemove', (e) => {
            if (tooltipRef.current && tooltipRef.current.style.display === 'block') {
                tooltipRef.current.style.left = (e.originalEvent.clientX + 16) + 'px';
                tooltipRef.current.style.top = (e.originalEvent.clientY + 16) + 'px';
            }
        });
        kaart.on('mouseout', () => { resetActief(); hideTip(); });

        const legenda = L.control({ position: 'bottomleft' });
        legenda.onAdd = () => {
            const div = L.DomUtil.create('div', 'sft-omzet-legenda');
            div.style.cssText = 'background:#fff;border-radius:8px;padding:12px 16px;box-shadow:0 2px 12px rgba(0,0,0,0.2);min-width:180px;line-height:1.4;color:#444;';
            div.innerHTML = `
                <div style="font-size:12px;font-weight:600;color:#444;margin-bottom:10px;text-transform:uppercase;letter-spacing:0.5px;">${tx('webreport_map_legend', 'Revenue')}</div>
                <div style="height:14px;border-radius:3px;margin-bottom:6px;background:linear-gradient(to right,#eff3ff,#bdd7e7,#6baed6,#2171b5,#084594);"></div>
                <div class="sft-omzet-legenda-labels" style="display:flex;justify-content:space-between;font-size:11px;color:#666;">
                    <span>${formateerOmzet(minRef.current)}</span><span>${formateerOmzet(maxRef.current)}</span>
                </div>
                <div style="display:flex;align-items:center;gap:8px;margin-top:10px;font-size:11px;color:#666;">
                    <div style="width:20px;height:14px;background:#e8e8e8;border:1px solid #ccc;border-radius:2px;flex-shrink:0;"></div>${tx('webreport_map_noData', 'No data')}
                </div>`;
            return div;
        };
        legenda.addTo(kaart);
        legendRef.current = legenda;

        setMapReady(true);
        setTimeout(() => { try { kaart.invalidateSize(); } catch (e) { /* noop */ } }, 250);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (open && !loadingFilters && stores.length >= 0) buildMap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, loadingFilters]);

    // Tear down on close so reopening rebuilds cleanly (GeoJSON stays module-cached → instant).
    useEffect(() => {
        if (open) return;
        if (mapRef.current) { try { mapRef.current.remove(); } catch (e) { /* noop */ } }
        mapRef.current = null;
        layerRef.current = null;
        legendRef.current = null;
        setMapReady(false);
    }, [open]);

    // ── Recolor whenever data/min/max change ──────────────────────────────────
    const recolor = useCallback(() => {
        rebuildCityTotals();
        if (layerRef.current) layerRef.current.setStyle((feature) => {
            const omzet = cityTotalsRef.current[feature.properties.city];
            return {
                fillColor: omzetNaarKleur(omzet, minRef.current, maxRef.current),
                fillOpacity: omzet ? 0.80 : 0.25,
                color: '#fff', weight: 0.5, opacity: 0.7,
            };
        });
        if (legendRef.current) {
            const c = legendRef.current.getContainer && legendRef.current.getContainer();
            const labels = c && c.querySelector('.sft-omzet-legenda-labels');
            if (labels) labels.innerHTML = `<span>${formateerOmzet(minRef.current)}</span><span>${formateerOmzet(maxRef.current)}</span>`;
        }
    }, [rebuildCityTotals]);

    // ── Fetch revenue (live) — stale responses are ignored via reqId ──────────
    const fetchRevenue = useCallback(() => {
        if (!open) return;
        if (!van || !tot || van.isAfter(tot)) return;
        const winkels = selectedStores;
        const kassas = selectedRegisters;
        const myReq = ++reqIdRef.current;
        if (winkels.length === 0 || kassas.length === 0) {
            dataRef.current = {};
            recolor();
            return;
        }
        setLoadingMap(true);
        SftGlobalData.post('webreports&action=mapRevenue', {
            key: props.api,
            van: van.format('YYYY-MM-DD'),
            tot: tot.format('YYYY-MM-DD'),
            winkels,
            kassas,
        }).then(response => {
            if (myReq !== reqIdRef.current) return; // a newer request superseded this one
            if (typeof response.error === 'undefined' && response?.data && typeof response.data === 'object') {
                dataRef.current = response.data;
                setErrorMessage(null);
            } else {
                dataRef.current = {};
                setErrorMessage(response?.message || tx('webreport_map_revenueError', 'Could not load revenue data'));
            }
            recolor();
            setLoadingMap(false);
        }).catch(() => {
            if (myReq !== reqIdRef.current) return;
            setLoadingMap(false);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, van, tot, selectedStores, selectedRegisters, props.api, recolor]);

    // Refetch on any filter change once the map exists (live, on-the-go).
    useEffect(() => {
        if (!mapReady) return;
        fetchRevenue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapReady, van, tot, selectedStores, selectedRegisters]);

    // ── Render ────────────────────────────────────────────────────────────────
    const storeOption = (code) => stores.find(s => s.code === code);
    const registerOption = (code) => registers.find(r => r.code === code);

    // Dropdown Paper with "Select all / Clear" quick actions (onMouseDown preventDefault
    // keeps the popup open and the input focused).
    const makeActionPaper = (onAll, onNone) => (paperProps) => (
        <Paper {...paperProps} elevation={8}>
            <Box
                onMouseDown={(e) => e.preventDefault()}
                sx={{ display: 'flex', gap: 1, p: 1, position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper' }}
            >
                <Button size="small" fullWidth variant="outlined"
                    onMouseDown={(e) => { e.preventDefault(); onAll(); }}>
                    {tx('webreport_map_selectAll', 'Select all')}
                </Button>
                <Button size="small" fullWidth variant="outlined" color="inherit"
                    onMouseDown={(e) => { e.preventDefault(); onNone(); }}>
                    {tx('webreport_map_clear', 'Clear')}
                </Button>
            </Box>
            <Divider />
            {paperProps.children}
        </Paper>
    );

    const storePaper = useMemo(
        () => makeActionPaper(() => setSelectedStores(stores.map(s => s.code)), () => setSelectedStores([])),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [stores, props.lang]
    );
    const registerPaper = useMemo(
        () => makeActionPaper(() => setSelectedRegisters(availableRegisters.map(r => r.code)), () => setSelectedRegisters([])),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [availableRegisters, props.lang]
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth={false} fullWidth
            TransitionProps={{ onEntered: () => { if (mapRef.current) setTimeout(() => mapRef.current.invalidateSize(), 50); } }}
            PaperProps={{ sx: { m: 3, width: 'calc(100% - 48px)', maxWidth: 'calc(100% - 48px)', height: 'calc(100% - 48px)', maxHeight: 'calc(100% - 48px)' } }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 6 }}>
                <MapOutlinedIcon color="primary" />
                <Typography variant="h6" component="span" sx={{ flex: 1 }}>
                    {tx('webreport_map_title', 'Revenue map (Belgium)')}
                </Typography>
                {loadingMap && <CircularProgress size={20} />}
                <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 2 }}>
                {/* Live filter bar */}
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                    <DatePicker
                        label={tx('webreport_map_from', 'From')}
                        value={van}
                        onChange={(v) => v && setVan(v)}
                        format="DD/MM/YYYY"
                        slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                    />
                    <DatePicker
                        label={tx('webreport_map_to', 'To')}
                        value={tot}
                        onChange={(v) => v && setTot(v)}
                        format="DD/MM/YYYY"
                        slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                    />
                    <Autocomplete
                        multiple
                        size="small"
                        disableCloseOnSelect
                        options={stores.map(s => s.code)}
                        value={selectedStores}
                        onChange={(e, v) => setSelectedStores(v)}
                        getOptionLabel={(code) => storeOption(code)?.oms || code}
                        renderOption={(liProps, code, { selected }) => (
                            <li {...liProps} key={code}>
                                <Checkbox {...icon} style={{ marginRight: 8 }} checked={selected} />
                                {storeOption(code)?.oms || code}
                            </li>
                        )}
                        renderTags={(value, getTagProps) =>
                            value.length === stores.length
                                ? <Chip size="small" label={tx('webreport_map_allShops', 'All shops')} {...getTagProps({ index: 0 })} />
                                : value.map((code, i) => <Chip size="small" label={storeOption(code)?.oms || code} {...getTagProps({ index: i })} key={code} />)
                        }
                        renderInput={(params) => <TextField {...params} label={tx('webreport_map_shops', 'Shops')} />}
                        PaperComponent={storePaper}
                        sx={{ minWidth: 240, flex: 1 }}
                    />
                    <Autocomplete
                        multiple
                        size="small"
                        disableCloseOnSelect
                        options={availableRegisters.map(r => r.code)}
                        value={selectedRegisters}
                        onChange={(e, v) => setSelectedRegisters(v)}
                        getOptionLabel={(code) => registerOption(code)?.oms || code}
                        renderOption={(liProps, code, { selected }) => (
                            <li {...liProps} key={code}>
                                <Checkbox {...icon} style={{ marginRight: 8 }} checked={selected} />
                                {registerOption(code)?.oms || code}
                            </li>
                        )}
                        renderTags={(value, getTagProps) =>
                            value.length === availableRegisters.length
                                ? <Chip size="small" label={tx('webreport_map_allRegisters', 'All registers')} {...getTagProps({ index: 0 })} />
                                : value.map((code, i) => <Chip size="small" label={registerOption(code)?.oms || code} {...getTagProps({ index: i })} key={code} />)
                        }
                        renderInput={(params) => <TextField {...params} label={tx('webreport_map_registers', 'Registers')} />}
                        PaperComponent={registerPaper}
                        sx={{ minWidth: 240, flex: 1 }}
                    />
                </Stack>

                {errorMessage && <Alert severity="error" onClose={() => setErrorMessage(null)}>{errorMessage}</Alert>}

                {/* Map */}
                <Box sx={{ position: 'relative', flex: 1, minHeight: 0, borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                    <div ref={containerRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
                    <div
                        ref={tooltipRef}
                        style={{
                            position: 'fixed', display: 'none', pointerEvents: 'none', zIndex: 2000,
                            background: 'rgba(20,20,40,0.88)', color: '#fff', padding: '10px 14px',
                            borderRadius: 8, fontSize: 13, boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                        }}
                    />
                    {(!mapReady || loadingFilters) && (
                        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.04)', zIndex: 500 }}>
                            <Stack alignItems="center" spacing={1}>
                                <CircularProgress />
                                <Typography variant="body2" color="text.secondary">{tx('webreport_map_loading', 'Loading map…')}</Typography>
                            </Stack>
                        </Box>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default RevenueMapModal;
